// src/components/ThemeSelector.js - Complete file with lightbox and fixes
import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Slider from "react-slick";
import { themeList, applyTheme } from '../themes';
import styles from './ThemeSelector.module.css';
import { Crown } from 'lucide-react';
import ThemeLightbox from './ThemeLightbox'; // Import the new component

// Import the ProFeatureContext
import { useProFeature } from '../context/ProFeatureContext';

// Import react-slick styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Log themes on initial import to check they're loaded correctly
console.log("DEBUG: Imported themeList:", themeList);

// Define theme-specific link background opacity values
const themeLinkOpacities = {
  'classic': 100,
  'gold': 80,
  'minimal': 60,
  'bold': 100,
  // Add other themes as needed
};

const ThemePreviewCard = ({ theme, isSelected, onSelect, isProUser, onProFeatureClick, onPreviewClick }) => {
  const isPro = theme.tier === 'pro';
  const isLocked = isPro && !isProUser;
  
  const handleClick = () => {
    if (isLocked) {
      // If it's a pro theme and user is not pro, trigger pro notification
      if (onProFeatureClick) {
        onProFeatureClick();
      }
    } else {
      // Show preview in lightbox instead of directly applying
      if (onPreviewClick) {
        onPreviewClick(theme);
      }
    }
  };
  
  return (
    <div 
      className={`${styles.themeCard} ${isSelected ? styles.selected : ''} ${isLocked ? styles.locked : ''}`}
      onClick={handleClick}
    >
      <div className={styles.themeImageContainer}>
        {isPro && (
          <div className={styles.proTag}>
            <Crown size={12} />
            <span>PRO</span>
          </div>
        )}
        <div className={styles.themeImage}>
          {/* Use the preview path from the theme object */}
          <img 
            src={theme.preview} 
            alt={`${theme.name} theme preview`} 
            className={styles.themePreviewImage}
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className={styles.placeholderImage} style={{ display: 'none' }}>
            <span>{theme.name} Preview</span>
          </div>
          
          {/* Show lock overlay for locked themes */}
          {isLocked && (
            <div className={styles.lockedOverlay}>
              <Crown size={24} />
              <span>Pro Only</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.themeName}>
        {theme.name}
        {isPro && !isProUser && <span className={styles.smallProBadge}><Crown size={10} /></span>}
      </div>
    </div>
  );
};

// Grid of theme cards - each grid will be a slide in the carousel
const ThemeGrid = ({ themes, selectedTheme, onSelect, isProUser, onProFeatureClick, onPreviewClick }) => {
  return (
    <div className={styles.themeGrid}>
      {themes.map((theme) => (
        <ThemePreviewCard
          key={theme.id}
          theme={theme}
          isSelected={selectedTheme === theme.id}
          onSelect={onSelect}
          isProUser={isProUser}
          onProFeatureClick={onProFeatureClick}
          onPreviewClick={onPreviewClick} // Pass the preview handler
        />
      ))}
    </div>
  );
};

const ThemeSelector = ({ userId, initialTheme, onSave, userData, isProUser, onUpgradeClick }) => {
  // Add renderKey to force re-renders
  const [renderKey, setRenderKey] = useState(0);
  
  // Debug log the available themes
  console.log("Available themes:", themeList);
  console.log("Initial theme:", initialTheme);
  
  const [selectedTheme, setSelectedTheme] = useState(initialTheme || 'classic');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'free', or 'pro'
  
  // Debug the current selected theme
  console.log("Current selected theme ID:", selectedTheme);
  
  // Get the selected theme object from the list
  const selectedThemeObject = themeList.find(t => t.id === selectedTheme) || themeList[0];
  console.log("Selected theme object:", selectedThemeObject);
  
  // New state for lightbox preview
  const [previewTheme, setPreviewTheme] = useState(null);
  
  // Log props for debugging
  console.log("ThemeSelector props:", { userId, initialTheme, isProUser, userData });
  
  // Access the ProFeatureContext
  const { showProFeatureNotification } = useProFeature();
  
  // Handle Pro feature click - displays notification
  const handleProFeatureClick = () => {
    // Show prominent notification for pro feature
    showProFeatureNotification('Upgrade to Pro to access premium themes');
    
    // Shake the upgrade button or other visual indication
    if (onUpgradeClick) {
      // You might add a visual indication here
    }
  };

  // Filter themes based on tier
  const filteredThemes = filter === 'all' 
    ? themeList 
    : themeList.filter(theme => theme.tier === filter);

  // Chunk themes into groups of 6 for each carousel slide
  const themeChunks = [];
  const chunkSize = 6; // 6 themes per page (3x2 grid)
  
  for (let i = 0; i < filteredThemes.length; i += chunkSize) {
    themeChunks.push(filteredThemes.slice(i, i + chunkSize));
  }

  // Handle theme preview click - open lightbox
  const handlePreviewClick = (theme) => {
    console.log("Opening preview for theme:", theme.name);
    setPreviewTheme(theme);
  };
  
  // Handle apply from lightbox
  const handleApplyTheme = async () => {
    if (!previewTheme) return;
    
    try {
      const themeId = previewTheme.id;
      console.log("Applying theme with ID:", themeId);
      
      // Set state FIRST before any async operations
      setSelectedTheme(themeId);
      
      // Force a re-render by updating the renderKey
      setRenderKey(prev => prev + 1);
      
      // Get the theme's default link opacity
      const themeOpacity = themeLinkOpacities[themeId] || 100;
      
      // Apply theme immediately for visual feedback
      applyTheme(themeId);
      
      // Also set the link background opacity CSS variable directly
      document.documentElement.style.setProperty('--link-background-opacity', themeOpacity / 100);
      
      // Get the current user document to access current palette data
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      // Get current palette or create empty one if it doesn't exist
      const currentPalette = userData?.profile?.palette || {};
      
      // Update the palette with the theme's default link opacity
      // and reset the linkOpacitySet flag to use theme defaults
      const updatedPalette = {
        ...currentPalette,
        linkOpacity: themeOpacity,
        linkOpacitySet: false  // Reset this flag so theme defaults take effect
      };
      
      // Save both theme and updated palette to database
      await updateDoc(userDocRef, {
        'profile.theme': themeId,
        'profile.palette': updatedPalette
      });
      
      // Notify parent component
      if (onSave) {
        onSave(themeId);
      }
      
      // Show success message
      setMessage('Theme applied successfully!');
      setTimeout(() => setMessage(''), 3000);
      
      // Verify the theme was applied correctly
      console.log("Theme applied, selected theme is now:", themeId);
      console.log("Selected theme object should be:", themeList.find(t => t.id === themeId));
      
      // Failsafe: Direct DOM update as a backup
      setTimeout(() => {
        try {
          const nameElement = document.querySelector(`.${styles.selectedThemeName}`);
          const descElement = document.querySelector(`.${styles.selectedThemeDescription}`);
          
          if (nameElement && themeList.find(t => t.id === themeId)) {
            nameElement.textContent = themeList.find(t => t.id === themeId).name;
          }
          
          if (descElement && themeList.find(t => t.id === themeId)) {
            descElement.textContent = themeList.find(t => t.id === themeId).description;
          }
          
          console.log("Forced DOM update for theme display");
        } catch (err) {
          console.error("Error in forced DOM update:", err);
        }
      }, 100);
      
      // Close the lightbox after everything else
      setPreviewTheme(null);
    } catch (error) {
      console.error('Error saving theme:', error);
      setMessage('Error applying theme. Please try again.');
      setTimeout(() => setMessage(''), 3000);
      // Close the lightbox
      setPreviewTheme(null);
    }
  };
  
  // Handle cancel from lightbox
  const handleCancelPreview = () => {
    setPreviewTheme(null);
  };

  // Apply theme on initial load and when selectedTheme changes
  useEffect(() => {
    console.log("Effect triggered, selectedTheme:", selectedTheme);
    if (selectedTheme) {
      applyTheme(selectedTheme);
    }
  }, [selectedTheme, initialTheme]);

  // Handle tier filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Carousel settings
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    swipeToSlide: true,
  };

  // Message class based on message content
  const messageClass = message.includes('requires') || message.includes('Upgrade') 
    ? styles.warningMessage 
    : styles.message;

  return (
    <div className={styles.themeSelector}>
      {/* Theme Lightbox Preview */}
      {previewTheme && (
        <ThemeLightbox 
          theme={previewTheme}
          onApply={handleApplyTheme}
          onCancel={handleCancelPreview}
        />
      )}
      
      {/* Theme display with renderKey to force updates */}
      <div className={styles.selectedThemeDisplay} key={`theme-display-${renderKey}-${selectedTheme}`}>
  <div className={styles.selectedThemeInfo}>
    <div className={styles.selectedThemeName}>
      {selectedThemeObject ? `Your selected theme is ${selectedThemeObject.name}` : "Select a theme for your profile"}
    </div>
    <div className={styles.selectedThemeDescription}>
      {selectedThemeObject ? selectedThemeObject.description : "Click on any theme below to preview and apply it."}
    </div>
    {message && <div className={messageClass}>{message}</div>}
  </div>
</div>

      <div className={styles.filterOptions}>
        <button 
          className={`${styles.filterButton} ${filter === 'all' ? styles.activeFilter : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All Themes
        </button>
        <button 
          className={`${styles.filterButton} ${filter === 'free' ? styles.activeFilter : ''}`}
          onClick={() => handleFilterChange('free')}
        >
          Free
        </button>
        <button 
          className={`${styles.filterButton} ${filter === 'pro' ? styles.activeFilter : ''}`}
          onClick={() => handleFilterChange('pro')}
        >
          <Crown size={12} /> Pro
        </button>
      </div>

      <div className={styles.carouselContainer}>
        <div className={styles.categoryTitleContainer}>
          <h2 className={styles.categoryTitle}>New Themes Added Every Week</h2>
        </div>
        
        {themeChunks.length > 0 ? (
          <Slider {...sliderSettings}>
            {themeChunks.map((chunk, index) => (
              <div key={index} className={styles.carouselSlide}>
                <ThemeGrid 
                  themes={chunk}
                  selectedTheme={selectedTheme}
                  onSelect={() => {}} // No longer used directly
                  isProUser={isProUser}
                  onProFeatureClick={handleProFeatureClick}
                  onPreviewClick={handlePreviewClick} // Pass preview handler
                />
              </div>
            ))}
          </Slider>
        ) : (
          <div className={styles.noThemesMessage}>
            No themes match the current filter.
          </div>
        )}
      </div>

      {!isProUser && (
        <div className={styles.upgradeContainer}>
          <p>Unlock all Pro themes and features with a Pro subscription.</p>
          <button className={styles.upgradeButton} onClick={onUpgradeClick}>
            <Crown size={14} /> Upgrade to Pro
          </button>
        </div>
      )}
    </div>
  );
};

export default ThemeSelector;